import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { useConfirmDataUnitModal } from './useConfirmDataUnitModal';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import { APIEventsEnum, useAppSelector, useSendEvent } from '@/hooks';
import { confirmDataUnit, getDataUnits } from '@/core/api';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate, formatNumber } from '@/core/helpers';
import { useTranslation } from 'react-i18next';
import { PropsWithChildren } from 'react';
import useNotification from '@/hooks/useNotification';
import { normalizeDateFE } from '@/core/normalization';
import { ResolveErrorModalSkeleton } from '../ResolveErrorModalSkeleton/ResolveErrorModalSkeleton';
import { TableGrid } from '../TableGrid/TableGrid';
import { SourceTotal } from '@/core/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';
import { AuthedRoutesEnum } from '@/core/enums';
import { useNPSModal } from '../NPSModal/useNPSModal';

const INVOICE_SOURCES: {
  [key: string]: string;
} = {
  amazon: 'Amazon',
  easybill: 'Easybill',
  billbee: 'Billbee',
  manual: 'Manually uploaded data',
  jtl: 'JTL',
};

export function ConfirmDataUnitModal({ children }: PropsWithChildren) {
  const { sendEvent } = useSendEvent();
  const { t } = useTranslation();
  const { vs6256StornoInvoice } = useFlags();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const activeCompany = useAppSelector(activeCompanySelector);
  const queryKey = useAppSelector(queryKeySelector);
  const { openModal: openNPSModal } = useNPSModal();

  const {
    activeDU,
    activeDUStats,
    isModalOpened,
    closeModal,
    activeDUDate,
    isLoading,
    sourceTotals,
    activeDuPPCount,
  } = useConfirmDataUnitModal();

  const filteredSourceTotals = sourceTotals.filter(
    (v) => v.data_source !== 'shipment'
  );

  return (
    <ConfirmModal
      isModalOpened={isModalOpened}
      closeModal={closeModal}
      isButtonsHidden={isLoading}
      confirm={
        !!activeDuPPCount
          ? () => navigate(AuthedRoutesEnum.PurchasePrices)
          : (setIsConfirm) => {
              if (!activeDU) return;
              if (!activeCompany) return;
              setIsConfirm(true);
              const apiEvent = APIEventsEnum.confirm_data_unit;
              confirmDataUnit(activeCompany.company_id, activeDU.id)
                .then(() => {
                  sendEvent(apiEvent, {
                    route: window.location.pathname,
                  });
                  return getDataUnits(activeCompany?.company_id!);
                })
                .then((DURes) => {
                  queryClient.setQueryData(
                    ['SellerDataUnits', queryKey],
                    DURes
                  );
                  setIsConfirm(false);
                  showNotification({
                    type: 'success',
                    text: t('Data for *period* has been confirmed').replace(
                      '*period*',
                      formatDate(normalizeDateFE(activeDU.date), 'MMMM yyyy')
                    ),
                  });
                  closeModal();
                  openNPSModal(apiEvent);
                })
                .catch(() => {
                  setIsConfirm(false);
                });
            }
      }
      confirmBtnText={!!activeDuPPCount ? 'Upload' : 'Confirm data'}
      title={
        !!activeDuPPCount
          ? 'Missing purchase prices'
          : t('Confirm data for *date*').replace(
              '*date*',
              activeDUDate ? formatDate(activeDUDate, 'MMMM yyyy') : ''
            )
      }
      name='ConfirmDataUnit'
    >
      {isLoading ? (
        <ResolveErrorModalSkeleton />
      ) : !!activeDuPPCount ? (
        <>
          <div
            className='mb-3 text-h4 font-extrabold'
            data-testid={`missing-pp-notification`}
          >
            {t('Upload missing purchase prices before confirming')}
          </div>
          <div className='mb-6'>
            {t(
              'Please upload the missing purchase prices by the deadline to avoid errors in your VAT returns. After uploading, you’ll be able to confirm the data.'
            )}
          </div>
        </>
      ) : (
        <div className='space-y-2'>
          {activeDUDate && (
            <div className='text-h4 font-extrabold'>
              {t(
                'Confirm that data for *date* is correct and complete'
              ).replace('*date*', formatDate(activeDUDate, 'MMMM yyyy'))}
            </div>
          )}
          {filteredSourceTotals.length > 0 && (
            <div className='px-6 pt-1'>
              <TableGrid<SourceTotal>
                headerClassName='text-gray-700 !font-normal !p-0'
                headerCellClassName='!px-0 text-end first:text-start'
                cellClassName='!px-0'
                rowClassName='!px-0 border-b border-b-gray-500 last:border-none !py-[6px]'
                columns={[
                  {
                    size: '1.9fr',
                    label: 'Data source',
                    field: 'data_source',
                    formatter: (item) => (
                      <div
                        data-testid={`${item.data_source}-data_source`}
                        className='font-semibold'
                      >
                        {t(
                          INVOICE_SOURCES[item.data_source] || item.data_source
                        )}
                      </div>
                    ),
                  },
                  {
                    label: 'Transactions',
                    field: 'transactions',
                    formatter: (item) => (
                      <div
                        data-testid={`${item.data_source}-transactions`}
                        className='text-end'
                      >
                        {formatNumber(item.transactions)}
                      </div>
                    ),
                  },
                ]}
                data={filteredSourceTotals}
              />
            </div>
          )}
          {children}
          <div className='!mt-4 rounded-md bg-gray-400 p-3'>
            {activeDUDate && (
              <div className='font-semibold'>
                ⚠&nbsp;
                {t(
                  'Please review that submitted invoices cover all sales for *date*'
                ).replace('*date*', formatDate(activeDUDate, 'MMMM yyyy'))}
              </div>
            )}
            <div>
              {t('Correct any errors or upload missing invoices if needed.')}
            </div>
            <div>
              <ul className='list-disc pl-6'>
                {activeDUStats && activeDUStats.warnings_count > 0 && (
                  <li>
                    {activeDUStats.warnings_count}{' '}
                    <span>
                      {t(
                        'invoices have notices but will be included to reports'
                      )}
                    </span>
                  </li>
                )}
                {vs6256StornoInvoice &&
                  activeDUStats &&
                  !!activeDUStats.to_be_corrected_count && (
                    <li>
                      {activeDUStats.to_be_corrected_count}{' '}
                      <span>
                        {t(
                          'invoices are "To be corrected" and must be corrected in your invoicing system.'
                        )}
                      </span>
                    </li>
                  )}
                {!vs6256StornoInvoice &&
                  activeDUStats &&
                  !!activeDUStats.muted_count && (
                    <li>
                      {activeDUStats.muted_count}{' '}
                      <span>
                        {t(
                          'invoices are "To be corrected" and must be corrected in your invoicing system.'
                        )}
                      </span>
                    </li>
                  )}
                {activeDUStats && activeDUStats.errors_count > 0 && (
                  <li className='font-semibold text-red-500'>
                    {activeDUStats.errors_count}{' '}
                    <span>
                      {t(
                        'invoices have errors and will not be included to reports'
                      )}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>
          {activeDUDate && (
            <div
              className='py-4'
              dangerouslySetInnerHTML={{
                __html: t(
                  'After confirmation, *month* data will be locked and cannot be edited and we will begin preparing your VAT filing. Changes or late submissions may incur extra charges or penalties. Please confirm ASAP to avoid delays.'
                ).replace('*month*', formatDate(activeDUDate, 'MMMM')),
              }}
            />
          )}
        </div>
      )}
    </ConfirmModal>
  );
}
